/**
 * ModuleHome — landing page for a top-level module (e.g. /alm, /risk).
 *
 * Reads from the new navigationConfig tree (Day 1 of the QYAS port). Used
 * to display a card grid of the module's available pages, grouped by
 * sub-section.
 *
 * Migrated from the OLD per-module `modules/<svc>/navigation.ts` flat
 * shape on 2026-05-19 (Day 4 of spec 2026-05-19-qfinpricer-portal-skin-qyas).
 */

import React from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Box, Typography, Grid, Card, CardActionArea, CardContent } from '@mui/material';
import { ArrowRight } from 'lucide-react';
import { MODULE_CONFIGS, type ModuleId, SECTION_ALIASES } from '../theme/moduleColors';
import {
  navigationConfig,
  type NavigationItem,
  type NavigationSection,
} from '../components/navigation/navigationConfig';

interface FlatItem {
  label: string;
  path: string;
}

/** Flatten a NavigationItem tree to leaf links (skipping pure parent nodes). */
function flatten(items: NavigationItem[]): FlatItem[] {
  const out: FlatItem[] = [];
  for (const item of items) {
    if (item.children?.length) {
      out.push(...flatten(item.children));
    } else {
      out.push({ label: item.label, path: item.path });
    }
  }
  return out;
}

/** A "section" for the home grid — either a top-level child of the module section
 *  (if it has children → becomes a sub-section), or a flat list. */
interface HomeSection {
  title: string;
  items: FlatItem[];
}

/** Convert a NavigationSection into one-or-more home-page sections. */
function sectionsForModule(section: NavigationSection): HomeSection[] {
  // Group: if a child is itself a parent (has children), it becomes its own section.
  // Otherwise the bare children are collected under the section's own label.
  const groups: HomeSection[] = [];
  const bare: FlatItem[] = [];
  for (const child of section.children) {
    if (child.children?.length) {
      groups.push({ title: child.label, items: flatten(child.children) });
    } else {
      bare.push({ label: child.label, path: child.path });
    }
  }
  if (bare.length > 0) {
    groups.unshift({ title: section.label, items: bare });
  }
  return groups;
}

const SECTIONS_BY_ID: Record<string, HomeSection[]> = Object.fromEntries(
  navigationConfig.map((s) => [s.id, sectionsForModule(s)]),
);

// The regenerated (v2) router passes moduleId explicitly; the path derivation
// below remains as the fallback so direct mounting keeps working.
const ModuleHome: React.FC<{ moduleId?: string }> = ({ moduleId: moduleIdProp }) => {
  const location = useLocation();
  const navigate = useNavigate();

  // Extract module ID from pathname, handling two-segment IDs like "reference-data".
  const segments = location.pathname.split('/').filter(Boolean);
  // Aliased ids ('instrument') are deliberately absent from MODULE_CONFIGS —
  // they must still pass this gate so the alias resolution below can run.
  const isKnown = (id: string) => id in MODULE_CONFIGS || id in SECTION_ALIASES;
  let moduleId: string | null = moduleIdProp && isKnown(moduleIdProp) ? moduleIdProp : null;
  if (segments.length >= 2) {
    const twoSeg = `${segments[0]}-${segments[1]}`;
    if (isKnown(twoSeg)) moduleId = twoSeg;
  }
  if (!moduleId && segments.length >= 1 && isKnown(segments[0])) {
    moduleId = segments[0];
  }

  // An aliased module id ('instrument') resolves to the hand module that owns
  // the concept ('instruments'), so its generated routes keep a home.
  const resolvedId = moduleId && SECTION_ALIASES[moduleId] ? SECTION_ALIASES[moduleId] : moduleId;
  const mod = resolvedId ? MODULE_CONFIGS[resolvedId as ModuleId] : null;
  const sections = resolvedId ? SECTIONS_BY_ID[resolvedId] : null;

  if (!mod || !sections) {
    return <Typography sx={{ color: 'text.primary' }}>Module not found</Typography>;
  }

  return (
    <Box>
      <Box mb={3}>
        <Typography variant="h4" sx={{ color: mod.accent }} gutterBottom>
          {mod.label}
        </Typography>
        <Typography variant="body1" sx={{ color: 'text.secondary' }}>
          {mod.description}
        </Typography>
      </Box>

      {sections.map((section) => (
        <Box key={section.title} mb={3}>
          <Typography
            variant="overline"
            sx={{
              color: 'text.secondary',
              fontWeight: 600,
              letterSpacing: 1.5,
              mb: 1.5,
              display: 'block',
            }}
          >
            {section.title}
          </Typography>
          <Grid container spacing={2}>
            {section.items.map((item) => (
              <Grid size={{ xs: 12, sm: 6, md: 4 }} key={item.path}>
                <Card
                  variant="outlined"
                  sx={{
                    borderColor: 'divider',
                    bgcolor: 'background.paper',
                    '&:hover': { borderColor: mod.accent },
                  }}
                >
                  <CardActionArea onClick={() => navigate(item.path)}>
                    <CardContent
                      sx={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                      }}
                    >
                      <Typography
                        variant="body1"
                        fontWeight={500}
                        sx={{ color: 'text.primary' }}
                      >
                        {item.label}
                      </Typography>
                      <ArrowRight size={16} color="inherit" />
                    </CardContent>
                  </CardActionArea>
                </Card>
              </Grid>
            ))}
          </Grid>
        </Box>
      ))}
    </Box>
  );
};

export default ModuleHome;
